<template>
    <div class="timelineItem" tabindex="0" @focus="itemFocused">
        <font-awesome-icon :icon="[item.milestoneType.fontAwesomeClass.split(' ')[0],
                                   item.milestoneType.fontAwesomeClass.substring(item.milestoneType.fontAwesomeClass.indexOf('-') + 1)]"
                           class="typeIcon" aria-hidden="true" />
        <div class="typeName">{{ localizedStringValue(item.milestoneType.nimi) }}</div>
        <div :class="[showDetails ? '' : 'hideText', 'itemText']">{{ localizedStringValue(item.milestone.nimi) }}</div>
        <div :id="'item-content-' + createItemContentId(item.milestone.uuid)" v-show="showDetails">
            <div class="status-and-date">
                <template v-if="!item.milestone.valmis && item.milestone.vanhaTavoitekuukausi && item.milestone.vanhaTavoitevuosi">
                    <span class="status"><span v-translate>to.be.ready</span>:</span>
                    <span>{{previousMonthAndYear(item.milestone, item.milestone)}}</span>
                    <font-awesome-icon :icon="['fa', 'arrow-right']" class="separator" aria-hidden="true" role="presentation"/>
                    <span>{{monthAndYear(item.milestone, item.milestone)}}</span>
                </template>
                <template v-else-if="!item.milestone.valmis">
                    <span class="status"><span v-translate>to.be.ready</span>:</span>
                    <span>{{monthAndYear(item.milestone, item.milestone)}}</span>
                </template>
                <template v-else>
                    <span class="status"><span v-translate>ready</span>:</span>
                    <span>{{monthAndYear(item.milestone, item.milestone)}}</span>
                </template>
            </div>
            <div class="ho-departments">
                <span class="departments" v-if="item.vastuuministeriot.length > 0">
                    <span class="departmentTitle mr-2" v-translate>responsible.ministry</span>
                    <template v-for="vastuuministerio in item.vastuuministeriot">
                        <span class="department-tag yja-tag">
                            {{ localizedStringValue(vastuuministerio.lyhenne) }}
                        </span>
                    </template>
                </span>
                <span class="departments" v-if="item.osavastuuministeriot.length > 0">
                    <span class="departmentTitle mr-2" v-translate>part.responsibility</span>
                    <template v-for="osavastuuministerio in item.osavastuuministeriot">
                        <span class="department-tag yja-tag">
                            {{ localizedStringValue(osavastuuministerio.lyhenne) }}
                        </span>
                    </template>
                </span>
            </div>
            <div class="links" v-if="milestoneLinkit.length > 0">
                <a v-for="linkki in milestoneLinkit" :href="linkki.url" target="_blank"><span v-translate>assignment.in.progress</span>: {{ linkki.tunnus }}</a>
                <br />
            </div>
            <div class="links" v-if="item.milestone.linkit.length > 0">
                <template v-for="linkki in item.milestone.linkit">
                    <a :href="localizedStringValue(linkki.url)" target="_blank">
                        {{ localizedStringValue(linkki.nimi) }}
                        <font-awesome-icon :icon="['fas', 'external-link-alt']" class="external-link-icon" aria-hidden="true" role="presentation"/>
                    </a>
                    <br />
                </template>
                <template v-for="linkki in item.kohteidenLinkit">
                    <a :href="localizedStringValue(linkki.url)" target="_blank">
                        {{ localizedStringValue(linkki.nimi) }}
                        <font-awesome-icon :icon="['fas', 'external-link-alt']" class="external-link-icon" aria-hidden="true" role="presentation"/>
                    </a>
                    <br />
                </template>
            </div>
        </div>
        <button type="button"
                class="showMoreToggle"
                :aria-label="showDetails ? t('show.less') : t('show.more')"
                @click="toggleDetails"
                :aria-controls="'item-content-' + createItemContentId(item.milestone.uuid)"
                :aria-expanded="showDetails ? 'true' : 'false'">
            <font-awesome-icon :icon="['fas', 'chevron-up']" class="close-trigger-icon" aria-hidden="true" role="presentation" v-if="showDetails"/>
            <font-awesome-icon :icon="['fas', 'chevron-down']" class="open-trigger-icon" aria-hidden="true" role="presentation" v-else/>
        </button>
    </div>
</template>

<script>
    import LocalizedStringService from "../services/localizedStringService";
    import DateService from "../services/dateService";

    const localizedStringService = new LocalizedStringService();
    const dateService = new DateService();

    export default {
        props: {
            item: Object,
        },
        data: function() {
            return {
                showDetails: this.item.showDetails,
                localeState: ""
            }
        },
        methods: {
            toggleDetails() {
                this.showDetails = !this.showDetails;
                this.$nextTick(function () {
                    this.$emit('itemToggled', this.showDetails);
                })
            },
            itemFocused() {
                this.$emit('focus');
            },
            localizedStringValue(localizedString) {
                return localizedStringService.getValue(localizedString, this.$translate.current);
            },
            monthAndYear(milestone) {
                return dateService.monthAndYear(milestone.tavoitekuukausi, milestone.tavoitevuosi);
            },
            previousMonthAndYear(milestone) {
                return dateService.monthAndYear(milestone.vanhaTavoitekuukausi, milestone.vanhaTavoitevuosi);
            },
            createItemContentId(id) {
                return id.substring(0, id.indexOf('-'));
            }
        },
        computed: {
            milestoneLinkit() {
                let linkit = [];
                this.item.milestone.liittyvatKohdeUuids.forEach(uuid => {
                    const linkki = this.item.kohdeLinkit[uuid];
                    if (linkki) {
                        linkit.push(linkki);
                    }
                });
                return linkit;
            }
        },
        beforeMount: function() {
            this.localeState = this.$translate.current;
        }
    }

</script>

<style lang="scss">
    $black : #222;
    $lightgray : #f0f0f0;
    $gray : #979797;
    $darkgray : #505050;
    $lightblue : #DFE7F5;
    $blue : #365abd;

    .aui .portlet-hankeikkuna-government-timeline, .portlet-hankeikkuna-government-timeline {

        .vis-timeline {

            .vis-item {

                &.vis-selected {
                    .vis-item-content {
                        .timelineItem {
                            .itemText {
                                max-height: none!important;
                            }

                            .showMoreToggle {
                                display: block;
                                background: $blue;
                            }
                        }
                    }
                }

                .vis-item-content {
                    background: white;

                    .timelineItem {
                        margin: 0;
                        padding: 10px 10px 10px 50px;
                        text-align: left;

                        .typeIcon {
                            font-size: 25px;
                            width: 25px;
                            color: $black;
                            position: absolute;
                            left: 10px;
                            top: 18px;
                        }

                        .typeName {
                            font-size: 12px;
                            font-weight: 600;
                            margin: 5px 0 0 0;
                            color: $darkgray;
                        }

                        img {
                            margin-right: 1em;
                            max-width: 24px;
                            width: 100%;
                            height: 24px !important;
                        }

                        .itemText {
                            position: relative;
                            white-space: normal;
                            font-size: 14px;
                            line-height: 1.4;
                            margin: 0 0 5px 0;

                            &.hideText {
                                max-height: 50px;
                                overflow: hidden;
                            }

                            @media (max-width: 768px) {
                                font-size: 13px;
                            }
                        }

                        .status-and-date  {
                            display: inline-block;
                            text-transform: uppercase;
                            color: $darkgray;
                            margin: 5px 0 5px 0;
                            position: relative;
                            font-size: 12px;

                            .status {
                                display: inline-block;
                                margin: 0 5px 0 0;
                                font-size: 11px;
                            }

                            .separator {
                                display: inline-block;
                                margin: 0 10px;
                                border: 0;
                            }
                        }

                        .ho-departments {
                            margin: 5px 0 0 0;
                            text-align: left;

                            .departments {
                                display: inline-block;
                                margin: 0 0 5px 0;
                                &:first-child {
                                    margin-right: 10px;
                                }
                            }

                            .departmentTitle {
                                font-size: 11px;
                                text-transform: uppercase;
                                color: $darkgray;
                            }

                            .department-tag {
                                text-align: left;
                                white-space: normal;
                                margin: 0 5px 5px 0;
                                font-size: 11.75px !important;
                            }
                        }

                        .links {
                            a {
                                font-size: 12px;
                                display: inline-block;

                                .external-link-icon {
                                    font-size: 11px;
                                    width: initial;
                                    margin: 0 0 0 5px;
                                }
                            }
                        }

                        .showMoreToggle {
                            display: none;
                            position: absolute;
                            left: 50%;
                            transform: translateX(-50%);
                            bottom: -15px;
                            margin: 0;
                            padding: 0;
                            background: $darkgray;
                            font-size: 16px;
                            height: 28px;
                            width: 28px;
                            color: white;
                            border-radius: 25px;
                            box-shadow: none;
                            border: 0;

                            svg {
                                margin: 1px 0 0 0;
                                height: 28px;

                                &.close-trigger-icon {
                                    margin: -1px 0 0 0;
                                }
                            }
                        }

                        @media (max-width: 768px) {
                            padding: 10px;

                            .typeIcon {
                                display: none;
                            }
                        }
                    }
                }
            }
        }
    }
</style>
